import { Link } from "react-router-dom";
import { motion, useReducedMotion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { Home as HomeIcon, HeartHandshake, UtensilsCrossed, Car, Flag, Users, Sparkles, MapPin, ChevronDown, ClipboardList, Briefcase } from "lucide-react";
import { SEO, orgJsonLd, websiteJsonLd, webPageJsonLd, breadcrumbJsonLd } from "@/components/SEO";
import { MaskedLines, Reveal } from "@/components/Reveal";
import { Marquee } from "@/components/Marquee";
import { CTABand } from "@/components/CTABand";
import { BUSINESS, img, srcSet, DIMS } from "@/data/site";

const WHY = [
  { icon: HomeIcon, title: "Small & Personal", copy: "Our intimate residential setting allows us to provide a more personal experience and get to know each resident individually." },
  { icon: Sparkles, title: "A Home-Like Environment", copy: "Savannah’s House is designed to feel like home—not an institution." },
  { icon: HeartHandshake, title: "Compassionate Support", copy: "We believe every resident deserves patience, kindness, dignity, and respect." },
  { icon: UtensilsCrossed, title: "Meals Included", copy: "Residents enjoy meals as part of their residential experience." },
  { icon: Car, title: "Transportation Assistance", copy: "Transportation assistance may be available for appropriate appointments, errands, and activities." },
  { icon: Flag, title: "Veteran Friendly", copy: "We proudly welcome veterans and strive to help connect them with appropriate community resources." },
];

const WHO = [
  { imgName: "seniorcouple", title: "Seniors", copy: "Adults seeking a comfortable and supportive residential environment.", alt: "Smiling senior couple relaxing outdoors together" },
  { imgName: "hands", title: "Veterans", copy: "Veterans looking for safe, respectful housing and support.", alt: "Hands joined together in a gesture of support and trust" },
  { imgName: "living2", title: "Adults with Disabilities", copy: "Individuals who benefit from assistance with everyday living in a residential setting.", alt: "Warm shared living space in a residential home" },
  { imgName: "garden", title: "Families Seeking Options", copy: "Families looking for a smaller, more personal alternative to larger residential environments.", alt: "Welcoming home exterior with a well-kept front lawn" },
];

const Hero = () => {
  const reduce = useReducedMotion();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", reduce ? "0%" : "16%"]);

  return (
    <section ref={ref} className="relative min-h-[88vh] flex items-center overflow-hidden" data-testid="home-hero">
      <motion.div style={{ y }} className="absolute inset-0" aria-hidden="true">
        <motion.img
          src={img("exterior", 1600)}
          srcSet={srcSet("exterior")}
          sizes="100vw"
          width={DIMS.exterior[0]}
          height={DIMS.exterior[1]}
          alt=""
          fetchPriority="high"
          decoding="async"
          initial={reduce ? false : { scale: 1.15 }}
          animate={{ scale: 1.02 }}
          transition={{ duration: 2.6, ease: [0.22, 1, 0.36, 1] }}
          className="w-full h-full object-cover"
        />
      </motion.div>
      <div className="absolute inset-0 bg-gradient-to-r from-espresso/85 via-espresso/60 to-espresso/25" aria-hidden="true" />
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 w-full">
        <motion.p
          initial={reduce ? false : { opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.1 }}
          className="flex items-center gap-2 text-xs sm:text-sm uppercase tracking-[0.22em] text-gold font-bold"
        >
          <MapPin size={15} aria-hidden="true" />
          Serving Tyler, Texas & the surrounding East Texas community
        </motion.p>
        <MaskedLines
          lines={["A Safe Place to Call Home.", <span className="text-gold italic">A Family You Can Trust.</span>]}
          className="mt-5 font-serif text-5xl sm:text-7xl lg:text-8xl font-bold text-ivory tracking-tight leading-[1.04] max-w-4xl"
        />
        <motion.div
          aria-hidden="true"
          className="mt-7 h-1 w-28 bg-gold rounded-full origin-left"
          initial={reduce ? false : { scaleX: 0 }}
          animate={{ scaleX: 1 }}
          transition={{ duration: 0.9, delay: 0.9, ease: [0.22, 1, 0.36, 1] }}
        />
        <Reveal delay={0.55}>
          <p className="mt-6 text-base sm:text-xl text-ivory/90 leading-relaxed max-w-xl font-medium">
            At Savannah’s House, we believe everyone deserves a place where they feel safe, respected, comfortable, and
            valued. We provide a warm, supportive residential environment for seniors, veterans, and adults seeking a
            smaller, more personal place to call home.
          </p>
          <div className="mt-8 flex flex-col sm:flex-row gap-4">
            <Link
              to="/contact?intent=tour"
              data-testid="hero-schedule-tour-button"
              className="bg-gold text-charcoal text-center font-bold px-8 py-4 rounded-full hover:bg-ivory hover:-translate-y-0.5 transition-all shadow-lift"
            >
              Schedule a Tour
            </Link>
            <Link
              to="/contact?intent=info"
              data-testid="hero-request-info-button"
              className="border-2 border-ivory/70 text-ivory text-center font-bold px-8 py-4 rounded-full hover:bg-ivory hover:text-charcoal hover:-translate-y-0.5 transition-all"
            >
              Request Information
            </Link>
          </div>
        </Reveal>
        <motion.div
          aria-hidden="true"
          className="absolute bottom-6 left-1/2 -translate-x-1/2 text-ivory/80"
          animate={reduce ? undefined : { y: [0, 10, 0] }}
          transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
        >
          <ChevronDown size={30} />
        </motion.div>
      </div>
    </section>
  );
};

const Chapter = ({ number, eyebrow, children, className = "" }) => (
  <div className={className}>
    <div className="flex items-baseline gap-4">
      <span className="font-serif text-5xl sm:text-6xl text-gold/70 font-medium leading-none" aria-hidden="true">
        {number}
      </span>
      <p className="text-xs sm:text-sm uppercase tracking-[0.22em] text-sage font-semibold">{eyebrow}</p>
    </div>
    {children}
  </div>
);

const Home = () => {
  const origin = typeof window !== "undefined" ? window.location.origin : "";
  return (
    <>
      <SEO
        title="Savannah's House | Senior Housing in Tyler, TX"
        description="Savannah's House is a warm, supportive residential home in Tyler, TX serving seniors, veterans, and adults across East Texas. Schedule a tour today."
        path="/"
        jsonLd={[
          orgJsonLd(origin),
          websiteJsonLd(origin),
          webPageJsonLd(origin, "/", "Savannah's House | Senior Housing in Tyler, TX", "A warm, supportive residential home in Tyler, Texas."),
          breadcrumbJsonLd(origin, [{ name: "Home", path: "/" }]),
        ]}
      />
      <Hero />
      <Marquee />

      <section className="py-12 sm:py-16 lg:py-20" data-testid="welcome-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-center">
          <Reveal className="lg:col-span-6">
            <Chapter number="01" eyebrow="Welcome to Savannah’s House">
              <h2 className="mt-5 font-serif text-3xl sm:text-4xl lg:text-5xl font-semibold text-espresso tracking-tight leading-tight text-balance">
                More Than a House. <span className="text-gold italic">A Place to Call Home.</span>
              </h2>
            </Chapter>
            <div className="mt-6 space-y-4 text-base sm:text-lg text-warmgray leading-relaxed">
              <p>
                At Savannah’s House, we believe everyone deserves to feel safe, respected, valued, and cared for.
                Founded by <strong className="text-espresso font-semibold">Kimberly Davis</strong>, a CNA and caregiver
                with more than 20 years of experience, Savannah’s House was created from a passion for helping others
                and a deep understanding of what families need when choosing a place for their loved ones.
              </p>
              <p>
                Our goal is simple: to provide a comfortable, supportive, home-like environment where residents can
                maintain their dignity and independence while receiving the care and support they need.
              </p>
              <p className="font-serif text-xl sm:text-2xl text-espresso italic">
                Because at Savannah’s House, we don’t just provide a place to live—we create a place to belong.
              </p>
            </div>
          </Reveal>
          <Reveal delay={0.15} className="lg:col-span-6">
            <div className="relative">
              <img
                src={img("living1", 1600)}
                srcSet={srcSet("living1")}
                sizes="(max-width: 1024px) 100vw, 50vw"
                width={DIMS.living1[0]}
                height={DIMS.living1[1]}
                alt="Comfortable, light-filled living room at a welcoming residential home"
                loading="lazy"
                decoding="async"
                className="rounded-3xl shadow-lift w-full aspect-[4/3] object-cover"
              />
              <p className="mt-3 text-xs text-warmgray italic">
                Representative photography — Savannah’s House photos coming soon.
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="bg-cream py-12 sm:py-16" data-testid="why-families-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal className="max-w-2xl">
            <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-semibold text-espresso tracking-tight text-balance">
              Why Families Choose Savannah’s House
            </h2>
          </Reveal>
          <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {WHY.map((w, i) => (
              <Reveal key={w.title} delay={(i % 3) * 0.08}>
                <article className="group h-full bg-white rounded-2xl p-6 sm:p-8 border border-sand/40 shadow-soft hover:shadow-lift hover:-translate-y-1 hover:border-laurel/60 transition-all duration-300">
                  <w.icon size={30} className="text-laurel transition-transform duration-300 group-hover:scale-110 group-hover:-rotate-6" aria-hidden="true" />
                  <h3 className="mt-4 font-serif text-xl sm:text-2xl font-semibold text-espresso">{w.title}</h3>
                  <p className="mt-2.5 text-sm sm:text-base text-warmgray leading-relaxed">{w.copy}</p>
                </article>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="py-12 sm:py-16" data-testid="who-we-welcome-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal className="max-w-2xl">
            <p className="text-xs sm:text-sm uppercase tracking-[0.22em] text-sage font-semibold">Who We Welcome</p>
            <h2 className="mt-3 font-serif text-3xl sm:text-4xl lg:text-5xl font-semibold text-espresso tracking-tight text-balance">
              A Smaller, More Personal Place to Call Home
            </h2>
          </Reveal>
          <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {WHO.map((w, i) => (
              <Reveal key={w.title} delay={(i % 4) * 0.07}>
                <article className="group h-full bg-white rounded-2xl overflow-hidden border border-sand/40 shadow-soft hover:shadow-lift hover:-translate-y-1 hover:border-laurel/60 transition-all duration-300">
                  <img
                    src={img(w.imgName, 900)}
                    srcSet={srcSet(w.imgName)}
                    sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 25vw"
                    width={DIMS[w.imgName][0]}
                    height={DIMS[w.imgName][1]}
                    alt={w.alt}
                    loading="lazy"
                    decoding="async"
                    className="aspect-[4/3] w-full object-cover transition-transform duration-500 group-hover:scale-105"
                  />
                  <div className="p-5 sm:p-6">
                    <h3 className="font-serif text-xl font-semibold text-espresso">{w.title}</h3>
                    <p className="mt-2 text-sm text-warmgray leading-relaxed">{w.copy}</p>
                  </div>
                </article>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-sagelight py-12 sm:py-16 lg:py-20" data-testid="dignity-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-center">
          <Reveal className="lg:col-span-5 order-2 lg:order-1">
            <img
              src={img("armchair", 900)}
              srcSet={srcSet("armchair")}
              sizes="(max-width: 1024px) 100vw, 42vw"
              width={DIMS.armchair[0]}
              height={DIMS.armchair[1]}
              alt="A comfortable armchair in soft sunlight — a quiet place to relax at home"
              loading="lazy"
              decoding="async"
              className="rounded-3xl shadow-lift w-full aspect-[3/4] object-cover"
            />
          </Reveal>
          <Reveal delay={0.1} className="lg:col-span-7 order-1 lg:order-2">
            <Chapter number="02" eyebrow="Our Promise">
              <h2 className="mt-5 font-serif text-3xl sm:text-4xl lg:text-5xl font-semibold text-espresso tracking-tight leading-tight text-balance">
                A Place Where Dignity Comes First
              </h2>
            </Chapter>
            <div className="mt-6 space-y-4 text-base sm:text-lg text-warmgray leading-relaxed">
              <p>We understand that accepting help can sometimes be difficult.</p>
              <p>
                That’s why our approach is centered around{" "}
                <strong className="text-espresso font-semibold">respect, compassion, independence, and dignity.</strong>
              </p>
              <p>We don’t want residents to feel like they are simply receiving services.</p>
              <p className="font-serif text-xl sm:text-2xl text-espresso italic">
                We want them to feel like they are coming home.
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="py-14 sm:py-20 lg:py-24 text-center" data-testid="brand-statement-section">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal>
            <p className="text-xs sm:text-sm uppercase tracking-[0.3em] text-gold font-bold">Savannah’s House</p>
            <h2 className="mt-6 font-serif text-3xl sm:text-5xl lg:text-6xl font-semibold text-espresso leading-tight tracking-tight text-balance">
              We don’t just provide a place to stay.
              <span className="block mt-2 text-sage">We create a place to belong.</span>
            </h2>
            <div className="mt-8 space-y-1.5 font-serif italic text-lg sm:text-2xl text-warmgray">
              <p>A safe place.</p>
              <p>A comfortable place.</p>
              <p>A supportive place.</p>
              <p>A place where every resident is treated with dignity.</p>
              <p className="pt-3 text-espresso not-italic font-semibold text-2xl sm:text-3xl">A place to call home.</p>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="bg-ivory py-12 sm:py-16 border-t border-sand/30" data-testid="next-steps-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal className="text-center max-w-2xl mx-auto">
            <p className="text-xs sm:text-sm uppercase tracking-[0.22em] text-sage font-semibold">Take the Next Step</p>
            <h2 className="mt-3 font-serif text-3xl sm:text-4xl font-semibold text-espresso tracking-tight text-balance">
              Three Simple Ways to Begin
            </h2>
          </Reveal>
          <div className="mt-8 grid grid-cols-1 sm:grid-cols-3 gap-5">
            <Reveal>
              <Link
                to="/waiting-list"
                data-testid="home-waiting-list-button"
                className="group block h-full bg-espresso text-ivory rounded-2xl p-6 sm:p-7 shadow-soft hover:shadow-lift hover:-translate-y-1 transition-all duration-300"
              >
                <ClipboardList size={28} className="text-gold transition-transform duration-300 group-hover:scale-110" aria-hidden="true" />
                <h3 className="mt-4 font-serif text-xl sm:text-2xl font-semibold">Join the Waiting List</h3>
                <p className="mt-2 text-sm text-ivory/70 leading-relaxed">
                  Our rooms fill quickly — be first to know when one opens.
                </p>
              </Link>
            </Reveal>
            <Reveal delay={0.08}>
              <Link
                to="/resident-inquiry"
                data-testid="home-resident-inquiry-button"
                className="group block h-full bg-white rounded-2xl p-6 sm:p-7 border border-sand/40 shadow-soft hover:shadow-lift hover:-translate-y-1 hover:border-laurel/60 transition-all duration-300"
              >
                <HeartHandshake size={28} className="text-laurel transition-transform duration-300 group-hover:scale-110" aria-hidden="true" />
                <h3 className="mt-4 font-serif text-xl sm:text-2xl font-semibold text-espresso">Start a Resident Inquiry</h3>
                <p className="mt-2 text-sm text-warmgray leading-relaxed">
                  Tell us about your loved one — no obligation, just a conversation.
                </p>
              </Link>
            </Reveal>
            <Reveal delay={0.16}>
              <Link
                to="/careers"
                data-testid="home-careers-button"
                className="group block h-full bg-sagelight rounded-2xl p-6 sm:p-7 border border-laurel/30 shadow-soft hover:shadow-lift hover:-translate-y-1 transition-all duration-300"
              >
                <Briefcase size={28} className="text-sage transition-transform duration-300 group-hover:scale-110" aria-hidden="true" />
                <h3 className="mt-4 font-serif text-xl sm:text-2xl font-semibold text-espresso">Careers — Join Our Team</h3>
                <p className="mt-2 text-sm text-warmgray leading-relaxed">
                  Compassionate caregivers: apply to do heart-first work with us.
                </p>
              </Link>
            </Reveal>
          </div>
        </div>
      </section>

      <CTABand
        eyebrow="Ready to Learn More?"
        heading="Let’s Find the Right Fit, Together"
        copy="Whether you’re looking for yourself, your parent, spouse, loved one, or a veteran in need of housing, we’re here to answer your questions."
        primary={{ label: "Schedule a Tour", to: "/contact?intent=tour", testid: "home-final-schedule-tour" }}
        secondary={{ label: "Contact Savannah’s House", href: "/contact", testid: "home-final-contact" }}
      />
    </>
  );
};

export default Home;
